import {
    AlertDialogCancel,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { LoadingButton } from "@/components/ui/loading-button";
import { Switch } from "@/components/ui/switch";
import { useAsset } from "@/hooks/use-asset";
import { useGetLoggedInUser } from "@/hooks/use-get-logged-in-user";
import { trpc } from "@/util/trpc";
import { useState } from "react";
import { toast } from "sonner";

export function ConfirmAlert({ onBuyButtonClicked, assetId }: { onBuyButtonClicked: () => void; assetId: number }) {
    const [termsAccepted, setAccepted] = useState(false);
    const { user: userProfile } = useGetLoggedInUser();
    const [marketingConsent, setMarketingConsent] = useState(userProfile?.marketingConsent ?? false);
    const [consentLoading, setConsentLoading] = useState(false);

    const { asset } = useAsset({ id: assetId });

    const updateMarketingConsent = trpc.lambda.users.updateMarketingConsent.useMutation();

    async function buyButtonClicked() {
        if (!termsAccepted) {
            toast.error("Error", { description: "Please accept the terms and conditions" });
            return;
        }

        if (marketingConsent) {
            try {
                setConsentLoading(true);
                await updateMarketingConsent.mutateAsync({ marketingConsent });
            } catch {
                toast.error("Error", { description: "Something went wrong" });
            } finally {
                setConsentLoading(false);
            }
        }

        onBuyButtonClicked();
    }

    return (
        <>
            <AlertDialogHeader>
                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                <AlertDialogDescription>Only invest what you can afford to loose.</AlertDialogDescription>
            </AlertDialogHeader>
            <div className="flex items-center gap-4">
                <Switch id="terms" checked={termsAccepted} onCheckedChange={setAccepted} />
                <Label htmlFor="terms">
                    I understand that this trade is executed directly on the blockchain platform.
                </Label>
            </div>
            {userProfile?.marketingConsent === false && (
                <div className="flex items-center gap-4">
                    <Switch id="marketing" checked={marketingConsent} onCheckedChange={setMarketingConsent} />
                    <Label className="inline" htmlFor="marketing">
                        I agree to share my contact information with{" "}
                        <span className="font-bold">{asset?.talent.username ?? "the Talent"}</span>
                    </Label>
                </div>
            )}
            <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <LoadingButton
                    loading={consentLoading}
                    onClick={() => {
                        void buyButtonClicked();
                    }}
                >
                    Confirm
                </LoadingButton>
            </AlertDialogFooter>
        </>
    );
}
